<template>
  <div class="c-breadcrumbs">
    <div class="c-breadcrumbs__inner">
      <div v-if="props.useFullPath">
        <FullPathBreadcrumb
          :breadcrumb="props.breadcrumb"
          :show-start="props.showStart"
          :to="route"
        />
      </div>
      <div v-else>
        <div class="c-breadcrumbs__inner__desktop">
          <FullPathBreadcrumb
            :breadcrumb="props.breadcrumb"
            :show-start="props.showStart"
            :to="route"
            :has-custom-link-behavior="props.hasCustomLinkBehavior"
            @click="onClick"
          />
        </div>
        <div class="c-breadcrumbs__inner__mobile">
          <ClosestParentBreadcrumb
            :base-path="props.basePath"
            :breadcrumb="props.breadcrumb"
            :has-filters-in-query="props.hasFiltersInQuery"
            :show-start="props.showStart"
            :to="route"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, onMounted } from 'vue'
import { CBreadcrumbsProps } from './CBreadcrumbs.props'
import FullPathBreadcrumb from './FullPathBreadcrumb'
import ClosestParentBreadcrumb from './ClosestParentBreadcrumb'

interface MetaBreadCrumbs {
  type: string
  json: {
    '@context': string
    '@type': string
    itemListElement: {
      '@type': string
      position: number
      name: string | undefined
      item: string
    }[]
  }
}

const props = withDefaults(defineProps<CBreadcrumbsProps>(), {
  breadcrumb: () => ({
    name: undefined,
    parents: [],
  }),
  basePath: '',
  baseUrl: '',
  showStart: true,
  hasFiltersInQuery: true,
  hasCustomLinkBehavior: false,
})

const emit = defineEmits<{
  (e: 'metaChange', meta: MetaBreadCrumbs): void
  (e: 'click', slug?: string): void
}>()

const metaBreadcrumbs = computed<MetaBreadCrumbs>(() => {
  const itemList = props.breadcrumb.parents
    ? props.breadcrumb.parents.map((breadcrumb, index) => {
        return {
          '@type': 'ListItem',
          position: index + 1,
          name: breadcrumb.name,
          item: `${props.baseUrl}${props.basePath}${breadcrumb.slug}`,
        }
      })
    : []
  return {
    type: 'application/ld+json',
    json: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: itemList,
    },
  }
})

const onClick = (slug?: string) => {
  if (props.hasCustomLinkBehavior) {
    emit('click', slug)
  }
}

const route = (parentSlug?: string) => {
  const route = useRoute()

  if (!route || !parentSlug) {
    return { path: '/' }
  }

  return {
    path: `${props.basePath}${parentSlug}`,
    query: props.hasFiltersInQuery ? route.query : undefined,
  }
}

onMounted(() => {
  emit('metaChange', metaBreadcrumbs.value)
})

watch(
  () => props.breadcrumb,
  () => {
    emit('metaChange', metaBreadcrumbs.value)
  },
)
</script>

<script lang="ts">
import { defineComponent } from 'vue'
import { useRoute } from 'vue-router'

export default defineComponent({
  name: 'CBreadcrumbs',
})
</script>
